<template>
  <div class="w-full">
    <template v-if="productInfo">
      <div class="container mb-2">
        <LazyUiBreadcrumbs />
      </div>

      <div class="container mb-2 flex w-full flex-col lg:mb-10 lg:flex-row">
        <div
          v-if="isEngagementRing"
          class="py-5 text-center text-xl font-light text-gray-100 lg:hidden"
        >
          <span
            v-if="isStartCustomizing"
            class="inline-block max-lg:max-w-[215px]"
          >
            {{ $t("designYourEngagementRing") }}
          </span>
          <span v-else data-e2e-item="product-title">
            {{ productTitle }}
          </span>
        </div>

        <div
          ref="stickyEl"
          class="w-full flex-auto overflow-hidden bg-white max-md:-mx-4 max-md:w-[calc(100%+2rem)]"
          :class="[
            isEngagementRing
              ? 'border-gray-30 transition-all duration-200 max-lg:border-b max-md:sticky max-md:top-[var(--header-height)] max-md:z-10 lg:max-w-[570px] 3xl:max-w-[748px]'
              : 'lg:max-w-[570px] xl:max-w-[748px]',

            isEngagementRing && isStuck ? 'max-md:px-[24%]' : '',
          ]"
        >
          <div
            ref="leftSide"
            v-sticky="lgAndUp && stickySide === 'left'"
            :sticky-offset="`{top: ${stickyOffsetTop}}`"
          >
            <div class="relative">
              <PagesProductAssetsSlider
                :images="images"
                :show-arrows="lgAndUp"
                :product-data="productInfo as ProductData"
                :is-diamond="false"
                :class="isEngagementRing ? 'mb-0' : 'mb-5'"
              />
              <ClientOnly>
                <UiButton
                  v-if="
                    configurationInfo &&
                    configurationInfo.configuration_id &&
                    !isEngagementRing
                  "
                  class="absolute right-6 top-4 z-10 flex-shrink-0 rounded-full bg-white lg:hidden"
                  variant="clear"
                  variant-type="icon"
                  size="xs"
                  :aria-label="$t('addToWishlist')"
                  data-e2e-btn="add-to-wishlist"
                  @click="onUpdateWishlist"
                >
                  <UiIcon
                    name="heart-bold"
                    class="size-3.5"
                    :class="[
                      inWishlist({
                        sku: configurationInfo?.configuration_id || '',
                        engraving: {},
                      })
                        ? 'fill-primary text-primary'
                        : '',
                    ]"
                    aria-hidden="true"
                  />
                </UiButton>
              </ClientOnly>
            </div>
          </div>
        </div>

        <div
          ref="rightSide"
          class="w-full flex-shrink-0 lg:ml-auto"
          :class="
            isEngagementRing
              ? 'lg:max-w-[424px] xl:max-w-[644px]'
              : 'lg:max-w-[424px]'
          "
        >
          <ProductEngagementProductInfo
            v-if="isEngagementRing"
            :seo-title="seoTitle"
            :product-title="productTitle"
            :current-configuration="currentConfiguration"
            :in-wishlist="
              Boolean(
                inWishlist({
                  sku: configurationInfo?.configuration_id || '',
                  engraving: {},
                }),
              )
            "
            :discount-value="configurationDiscountValue"
            :product-price="configurationPrice"
            :final-product-price="finalConfigurationPriceValue"
            :filters-pdp-many-in-one="filtersPDPManyInOne"
            :filtered-materials-by-categories="
              filteredMaterialsByCategoriesWithDisabled
            "
            :result="result"
            :additional-options="additionalOptions"
            :add-to-cart-loading="addToCartLoading"
            :details-maped-data="detailsMappedData"
            :product-description="productDescription"
            :shipping-returns-accordion-data="shippingReturnsAccordionData"
            :has-unavailable-material="hasUnavailableMaterial"
            :product-categories="(productInfo as ProductData)?.categories"
            :is-out-of-stock="isConfigurationOutOfStock"
            :start-at-price="configurationStartAtPrice"
            :is-filter-clicked="!isStartCustomizing"
            @add-to-cart="onAddToCart(null, true)"
            @options-filter="
              handleEngagementFilterClick(
                'optionsFilterClick',
                $event.slug,
                $event.item,
              )
            "
            @filter="
              handleEngagementFilterClick(
                'filterClick',
                $event.slug,
                $event.item,
              )
            "
            @replaceCurrentSku="currentSku = $event"
            @updateWishlist="onUpdateWishlist"
            @reinit-affirm="createAffirmMessage"
          />
          <ProductRegularProductInfo
            v-else
            :seo-title="seoTitle"
            :product-title="productTitle"
            :current-configuration="currentConfiguration"
            :in-wishlist="
              Boolean(
                inWishlist({
                  sku: configurationInfo?.configuration_id || '',
                  engraving: {},
                }),
              )
            "
            :discount-value="configurationDiscountValue"
            :product-price="configurationPrice"
            :final-product-price="finalConfigurationPriceValue"
            :filters-pdp-many-in-one="filtersPDPManyInOne"
            :filtered-materials-by-categories="
              filteredMaterialsByCategoriesWithDisabled
            "
            :result="result"
            :additional-options="additionalOptions"
            :add-to-cart-loading="addToCartLoading"
            :details-maped-data="detailsMappedData"
            :product-description="productDescription"
            :shipping-returns-accordion-data="shippingReturnsAccordionData"
            :has-unavailable-material="hasUnavailableMaterial"
            :track-inventory="(productInfo as ProductData).track_inventory"
            :product-categories="(productInfo as ProductData)?.categories"
            :is-out-of-stock="isConfigurationOutOfStock"
            :product-collections="(productInfo as ProductData)?.collections"
            :start-at-price="configurationStartAtPrice"
            :product-info="productInfo as ProductData"
            @add-to-cart="onAddToCart(null, true)"
            @options-filter="onOptionsFilterClick($event.slug, $event.item)"
            @filter="onFilterClick($event.slug, $event.item)"
            @updateWishlist="onUpdateWishlist"
          />
        </div>
      </div>
      <template v-if="isEngagementRing && !isConfigurationOutOfStock">
        <div
          v-sticky="true"
          sticky-side="bottom"
          class="!left-0 !right-0 z-50 !w-auto bg-white shadow-add-to-bag max-lg:hidden"
          :class="!isStartCustomizing ? 'block' : 'hidden'"
        >
          <div class="container flex items-center py-4">
            <div
              v-if="currentConfiguration?.data"
              class="ml-auto mr-11 flex items-center justify-center"
            >
              <div
                class="flex flex-wrap items-center gap-2.5 pb-2.5 text-xs font-light uppercase lg:py-1.5"
              >
                {{ $t("totalPrice") }}:
                <span class="text-2xl font-bold text-orange-50">{{
                  $n(finalConfigurationPriceValue, "currency")
                }}</span>
              </div>
              <div
                v-if="lgAndUp"
                class="ml-10 pt-2.5 max-lg:w-full lg:py-1.5 2xl:ml-18"
              >
                <ProductAffirmBlock />
              </div>
            </div>
            <div
              class="add-to-bag-section relative w-full flex-shrink-0 before:absolute before:left-0 before:top-1/2 before:block before:h-11 before:w-px before:-translate-y-1/2 before:bg-gray-70 lg:max-w-[424px] lg:pl-11 xl:max-w-[644px]"
            >
              <div class="add-to-bag-btn">
                <UiButton
                  :loading="addToCartLoading"
                  class="w-full uppercase"
                  size="lg"
                  :disabled="addToCartLoading || hasUnavailableMaterial"
                  data-e2e-btn="add-to-cart"
                  @click="onAddToCart"
                >
                  <span class="inline lg:hidden">
                    {{ $n(finalConfigurationPriceValue, "currency") }}
                    <span class="mx-3 inline-block">|</span>
                  </span>
                  {{ $t("addToBag") }}

                  <UiIcon name="bag" class="ml-2 size-6" />
                </UiButton>
              </div>
              <SharedFieldHint
                v-if="hasUnavailableMaterial"
                class="mt-4"
                :message="$t('unavailableConfiguration')"
                error
              />
            </div>
          </div>
        </div>
      </template>

      <div>
        <ProductListCarousel
          v-if="(productInfo as ProductData).related?.length"
          :products="
            (productInfo as ProductData).related as unknown as CatalogData[]
          "
          :title="$t('youMightAlsoLike')"
        />

        <PagesProductDiamondNexusExperience
          v-if="diamondNexusExperienceData"
          :data="diamondNexusExperienceData"
          :class="isEngagementRing ? 'lg:mx-0 lg:max-w-full lg:px-0' : ''"
        />

        <PagesHomeBlockRecentlyView
          v-if="recentlyViewData"
          :data="recentlyViewData"
        />
      </div>

      <!-- <DialogsImagePreviewDialog
      v-model="openImagePreview"
      :src="activeImage?.url || ''"
    /> -->
      <DialogsPurchaseProtection v-model="openPurchaseDialog" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useElementSize } from "@vueuse/core";
import { object, string } from "yup";
import { useForm } from "vee-validate";
// import { has } from "lodash-es";
import { FetchError } from "ofetch";
import {
  EmbedAddToCartData,
  EmbedWishListData,
  ScriptData,
  CatalogData,
  BlockData,
  RichTextModuleData,
  ProductData,
  assetsTypes,
} from "ecom-types";

import {
  propertiesGroupSlugs,
  sizesSlugs,
  staticProductCategoriesSlugs,
} from "@/configs";
import engagementRingPlaceholderImage from "@/assets/images/engagement-ring-placeholder.webp";
import {
  defaultConfigForDesignAndShape,
  defaultConfigForStyleShape,
} from "~/configs/engagementRingConfig";

const route = useRoute();
const router = useRouter();
const config = useRuntimeConfig();
const i18nHead = useLocaleHead({
  addSeoAttributes: true,
});
const { addToCart, cartItems, cartInfo } = useCart();
const { setBreadcrumbs, findBreadcrumb } = useBreadcrumbs();
const { inWishlist, updateWishlist } = useWishlist();
const generalStore = useGeneralStore();
const checkoutStore = useCheckoutStore();
const { openSystemDialog } = useSystemDialogs();
const { t, locale } = useI18n();
const { setProductPageLdSchema } = useJsonLdSchema();
const localePath = useLocalePathPolyfill();
const generalInternalStore = useGeneralInternalStore();

const { initStripeSource, createAffirmMessageElement } = useCheckout();

const { getIsIndivLoaded } = storeToRefs(generalStore);

const { setRecentlyViewed } = useRecentReview();

const { lgAndUp } = useAppBreakpoints();

const {
  addToCartEvent,
  visitEvent,
  eventsPriceFormat,
  dynamicParamsEvent,
  viewItemEvent,
} = useEvents();
const { stickyEl, isStuck } = useNativeStickyEl();

interface imagesData {
  url: string;
  alt?: string;
  type: string;
}

let affirmTimeout: NodeJS.Timeout;
const openPurchaseDialog = ref(false);
const isIndivInited = ref(false);
const addToCartLoading = ref(false);
const isMounted = ref(false);
const isStartCustomizing = ref(
  !!parseInt(route.query.start_customizing as string, 10),
);
const { headerMenu } = useGeneralStore();

const { $api } = useNuxtApp();

const { leftSide, rightSide, stickyOffsetTop, stickySide } = useSticky(155);

const [{ data: productResponse }, { data: blocksResponse }] = await Promise.all(
  [
    useAsyncCachedData(route.params.slug as string, () =>
      $api.products.getProductByIdOrSlug(route.params.slug as string),
    ),
    useAsyncCachedData("pdp-blocks", async () => {
      const [
        recentlyViewResponse,
        diamondNexusExperienceResponse,
        shippingReturnsPdpModuleResponse,
      ] = await Promise.all([
        $api.blocks.getBlock("home-recently-view"),
        $api.blocks.getBlock("diamond-nexus-experience"),
        $api.blocks.getBlock("shipping-returns-pdp-module"),
      ]);

      return {
        shippingReturnsPdpModuleResponse,
        diamondNexusExperienceResponse,
        recentlyViewResponse,
      };
    }),
  ],
);

const latestChangedCategory = ref("");

const {
  currentSku,
  result,
  additionalOptions,
  filteredMaterialsByCategories,
  hasUnavailableMaterial,
  filteredMaterialsByCategoriesWithDisabled,
  filtersPDPManyInOne,
  onFilterClick,
  onOptionsFilterClick,
  allSelectedMaterials,
  productInfo,
  configurationInfo,
  productTitle,
  productDescription,
  materialCategoriesDictionary,
  dynamicProductTitle,
  finalConfigurationPriceValue,
  configurationDiscountValue,
  oldConfigurationSKU,
  configurationPrice,
  configurationError,
  images: pdpImages,
  SizeMaterialCategory,
  configurationStartAtPrice,
  isConfigurationOutOfStock,
  isForceToPay,
} = usePdp(sizesSlugs, { pdpFilterOptions: { globalsSortOrder: true } });

productInfo.value = productResponse.value!.data;

const diamondNexusExperienceData = computed(() => {
  return blocksResponse.value?.diamondNexusExperienceResponse?.data
    ?.currentTranslation?.content;
});

const recentlyViewData = computed(() => {
  return blocksResponse.value?.recentlyViewResponse?.data?.currentTranslation
    ?.content;
});

const isEngagementRing = computed(() => {
  return (
    productInfo.value?.categories?.some(
      (el) => el.slug === staticProductCategoriesSlugs.engagement,
    ) || false
  );
});

const leadTime = computed(() => {
  return (
    configurationInfo.value?.lead_time ||
    (productInfo.value as ProductData)?.lead_time ||
    generalStore?.settings?.lead_time ||
    0
  );
});

const shippingReturnsAccordionData = computed(() => {
  const title =
    blocksResponse.value?.shippingReturnsPdpModuleResponse?.data
      ?.currentTranslation?.content?.title || t("shippingAndReturns");
  const content = replaceByPlaceholder(
    (
      blocksResponse.value?.shippingReturnsPdpModuleResponse
        ?.data as BlockData<RichTextModuleData>
    )?.currentTranslation?.content?.content || "",
    ["ship_date_pdp", "production_time"],
    [calculateShipDate(leadTime.value), leadTime.value],
  );
  return {
    title,
    content,
  };
});

const { data: currentConfiguration, error: currentConfigurationError } =
  await useAsyncCachedData(
    currentSku,
    () => {
      return $api.products.getConfiguration(currentSku);
    },
    { watch: [currentSku] },
  );

const { detailsMappedData } = useProductDetails();

const { validate } = useProductValidation();

watch(
  [() => currentConfigurationError.value, isMounted],
  ([val, mountedVal]) => {
    if ((val as FetchError)?.data?.errors?.[0]?.code === 404) {
      if (
        !oldConfigurationSKU.value &&
        (!productInfo.value?.default_sku ||
          productInfo.value?.default_sku === currentSku.value)
      ) {
        if (process.client) {
          if (!mountedVal) {
            return;
          }
          showError({
            statusCode: 404,
            statusMessage: `No such sku and no default sku`,
            fatal: true,
          });
        } else {
          showError({
            statusCode: 404,
            statusMessage: `No such sku and no default sku`,
            fatal: true,
          });
        }
        return;
      }
      if (!mountedVal) {
        return;
      }

      if (isEngagementRing.value) {
        const stylesResult =
          result.value[propertiesGroupSlugs.style] ||
          additionalOptions.value[propertiesGroupSlugs.style];

        const shapeResult =
          result.value[propertiesGroupSlugs.shape] ||
          additionalOptions.value[propertiesGroupSlugs.shape];

        const designResult =
          result.value[propertiesGroupSlugs.designCategory] ||
          additionalOptions.value[propertiesGroupSlugs.designCategory];

        if (designResult && shapeResult && stylesResult) {
          const designByStyle =
            defaultConfigForDesignAndShape[stylesResult.sku];
          const shapesByDesign = designByStyle[designResult.sku];
          if (latestChangedCategory.value === propertiesGroupSlugs.style) {
            const firstDesign = designByStyle[Object.keys(designByStyle)[0]];
            const firstShapeInDesign = firstDesign[Object.keys(firstDesign)[0]];
            if (designResult.sku in designByStyle) {
              const configWithCurrentShape =
                Object.values(designByStyle).find(
                  (design) => shapeResult.sku in design,
                )?.[shapeResult.sku] ||
                defaultConfigForStyleShape[stylesResult.sku][shapeResult.sku];

              currentSku.value =
                configWithCurrentShape ||
                oldConfigurationSKU?.value ||
                productInfo.value?.default_sku ||
                "";
            } else {
              const configWithCurrentShape =
                firstDesign[shapeResult.sku] || firstDesign[firstShapeInDesign];
              currentSku.value =
                configWithCurrentShape ||
                oldConfigurationSKU?.value ||
                productInfo.value?.default_sku ||
                "";
            }
          } else if (
            latestChangedCategory.value === propertiesGroupSlugs.designCategory
          ) {
            const configWithCurrentShape =
              shapesByDesign[shapeResult.sku] ||
              shapesByDesign[Object.keys(shapesByDesign)[0]];

            currentSku.value =
              configWithCurrentShape ||
              oldConfigurationSKU?.value ||
              productInfo.value?.default_sku ||
              "";
          } else {
            const configWithCurrentShape =
              shapesByDesign[shapeResult.sku] ||
              defaultConfigForStyleShape[stylesResult.sku][shapeResult.sku];

            currentSku.value =
              configWithCurrentShape ||
              oldConfigurationSKU?.value ||
              productInfo.value?.default_sku ||
              "";
          }
          configurationError.value = true;
        } else {
          currentSku.value =
            oldConfigurationSKU?.value || productInfo.value?.default_sku || "";
          configurationError.value = true;
        }
      } else {
        openSystemDialog({
          type: "alert",
          title: t("noSuchConfiguration"),
          text: t(
            "sorryButTheSelectedConfigurationDoesNotExistPleaseChooseAnyOther",
          ),
        });

        currentSku.value =
          oldConfigurationSKU?.value || productInfo.value?.default_sku || "";
        configurationError.value = true;
      }
    }
  },
  { immediate: true },
);

const images = computed<imagesData[]>(() => {
  if (isEngagementRing.value && isStartCustomizing.value) {
    return [
      {
        url: engagementRingPlaceholderImage,
        alt: t("designYourEngagementRing"),
        type: assetsTypes.image,
      },
    ];
  }
  return pdpImages.value;
});

watch(
  currentConfiguration,
  (val) => {
    configurationInfo.value = val?.data || null;
  },
  {
    immediate: true,
  },
);

watch(
  isForceToPay,
  async (val) => {
    if (!isConfigurationOutOfStock.value && val) {
      generalInternalStore.systemLoader = true;
      await onAddToCart(null, false);
      generalInternalStore.systemLoader = false;
    }
  },
  {
    immediate: true,
  },
);

const canInitAffirmMessage = computed(() => {
  return (
    finalConfigurationPriceValue.value &&
    checkoutStore.stripe.source &&
    configurationInfo.value &&
    isMounted.value
  );
});

watch(
  createAffirmMessage,
  () => {
    createAffirmMessage();
  },
  { immediate: true },
);

const { setNewBreadcrumbs } = useFullBreadcrumbs();

watch(
  allSelectedMaterials,
  (val) => {
    if (val && Object.keys(val).length) {
      setNewBreadcrumbs();
    }
  },
  { deep: true },
);

setProductPageLdSchema({
  name: productTitle,
  images,
  product: productInfo as Ref<ProductData>,
  price: configurationPrice,
  out_of_stock: isConfigurationOutOfStock,
  sku: configurationInfo.value?.configuration_id || "",
  description: productDescription,
});

const script = ref<Ref[]>([]);
const embedScript = ref<ScriptData>({
  hid: "extscript",
  // src: `http://localhost:4173/embedded.js?q=${Date.now()}`,
  src: `https://pdp3.devsyqor.com/embedded.js?q=${Date.now()}`,
  crossorigin: true,
  defer: true,
  onload: () => {
    generalStore.setIndevLoaded();
  },
});

if (productInfo.value?.embedded) {
  script.value.push(embedScript);
}

useHead(() => {
  if (i18nHead.value.link && config.public.domain) {
    const canonicalLink = i18nHead.value.link.find(
      (link) => link.rel === "canonical",
    );
    if (canonicalLink) {
      canonicalLink.href = config.public.domain + route.fullPath;
    }
  }

  return {
    meta: [
      ...metaGenerator({
        title:
          productInfo.value?.currentTranslation?.meta_title ||
          productTitle.value ||
          "",
        description:
          productInfo.value?.currentTranslation?.meta_description ||
          productDescription.value ||
          "",
        image: productInfo.value?.currentTranslation?.og_image?.url || "",
        url:
          productInfo.value?.currentTranslation?.meta_url ||
          config.public.domain + route.path,
      }),
      ...(i18nHead.value.meta || []),
    ],
    htmlAttrs: {
      lang: i18nHead.value.htmlAttrs!.lang,
    },
    link: [...(i18nHead.value.link || [])],
    // title:
    //   productData?.currentTranslation?.meta_title || productTitle.value || "",
    script,
  };
});

const { seoTitle, seoDescription } = usePageSeo();

useSeoMeta({
  title: () => seoTitle.value,
  ogTitle: () => seoTitle.value,
  description: () =>
    productInfo.value?.currentTranslation?.meta_description ||
    seoDescription.value ||
    "",
  ogDescription: () =>
    productInfo.value?.currentTranslation?.meta_description ||
    seoDescription.value ||
    "",
});

onMounted(async () => {
  setViewItemEvent(configurationInfo.value);
  isMounted.value = true;

  if (!isIndivInited.value && getIsIndivLoaded.value) {
    await window.confm8ht77.init({
      customer_number: productInfo.value?.customer_number || "",
      title: productTitle.value || "",
      default_sku: productInfo.value?.default_sku || "",
      // translations: this.$i18n.messages[this.$i18n.locale].configurator,
      discount:
        productInfo.value?.sale_value && productInfo.value?.sale
          ? productInfo.value.sale_value
          : null,
      currency: "USD",
      // lang: process.env.LOCALE,
      size_guide_link:
        productInfo.value?.currentTranslation?.size_chart?.url || null,
      // sticky_offset: 64,
      pdpAddToBag: onAddToCart,
      onWishlist,
      // onCompare: this.onCompare,
    });
    isIndivInited.value = true;
  }
  onSetRecentlyViewed();
  await initStripeSource();
});

onBeforeUnmount(() => {
  if (window?.confm8ht77?.destroy) {
    window.confm8ht77.destroy();
  }
  onSetRecentlyViewed();
});

function calculateShipDate(leadTimeInDays: number) {
  return leadTimeInDays + 3;
}

async function onAddToCart(
  data?: EmbedAddToCartData | null,
  openCardDrawer?: boolean,
) {
  const { valid } = await validate();

  if (!valid || !productInfo.value) {
    return;
  }

  addToCartLoading.value = true;
  try {
    const images: string[] = [];
    let images_ids: number[] = [];
    const options: { [key: string]: string | number | object | boolean } = {
      track_inventory: (productInfo.value as ProductData)?.track_inventory,
    };
    if (productInfo.value?.embedded) {
      options.cost = data?.cost || 0;
      options.engraving_options = JSON.stringify(data?.engraving);
      if (data?.images?.length) {
        images.push(...data.images);
      }
      options.configuration_id = data?.configuration_id || "";
    } else {
      const imageIdListIds = configurationInfo.value?.medias?.reduce(
        (acc, item) => {
          if (item?.position?.includes("PDP") && item?.file?.id) {
            acc.push(item.file.id);
          }
          return acc;
        },
        [] as number[],
      );
      let image: string | null = null;
      if (!imageIdListIds?.length) {
        image = await mergeImages([
          { src: configurationInfo.value?.medias?.[0]?.file?.url },
        ]);
      }
      if (image) {
        images.push(image);
      } else if (imageIdListIds?.length) {
        images_ids = imageIdListIds;
      }
    }
    if (Object.values(additionalOptions.value).length) {
      options.materials = Object.entries(additionalOptions.value).map(
        ([key, val]) => {
          return {
            sku: val?.sku,
            title: val?.currentTranslation?.title,
            material_category_title: filteredMaterialsByCategories.value.find(
              (el) => el.slug === key,
            )?.currentTranslation?.title,
            material_category_id: val?.material_category_id,
            material_category_sku: productInfo.value?.material_categories?.find(
              (category) => category.id === val?.material_category_id,
            ),
          };
        },
      );
    }
    await addToCart(
      {
        configuration_id: currentSku.value,
        vendor_model: productInfo.value?.vendor_model,
        images,
        images_ids,
        options,
      },
      openCardDrawer,
    );
    addToCartEvent({
      product_id: productInfo.value.id.toString(),
      configuration_id: configurationInfo.value?.configuration_id || "",
      collection_id: productInfo.value?.collections?.[0]?.id,
      product_sku: productInfo.value?.vendor_model,
      product_price: configurationPrice.value,
      productData: productInfo.value,
      configurationData: configurationInfo.value || undefined,
      productIndex: cartItems.value.length,
    });
    if (isForceToPay.value) {
      await router.replace(
        localePath({
          path: `/checkout/${cartInfo.value.unique_id}`,
        }),
      );
    }
  } catch (error) {
    if (error instanceof ImageMergeError) {
      openSystemDialog({
        type: "alert",
        title: `${t("error")}!`,
        text: `${t("failedLoadImage")}!`,
      });
    }
  } finally {
    addToCartLoading.value = false;
    generalInternalStore.systemLoader = false;
  }
}

async function onWishlist(data: EmbedWishListData) {
  const { valid } = await validate();

  if (!valid || !productInfo.value) {
    return;
  }

  await updateWishlist({
    ...data?.value,
    images: isArray(data.value.images)
      ? data.value.images
      : [data.value.images || ""],
    slug: productInfo.value?.slug || "",
    materials: productInfo.value?.materials || [],
    material_categories: productInfo.value?.material_categories,
    material_categories_plp_hover_ids:
      productInfo.value?.material_categories_plp_hover_ids,
    translations: productInfo.value.translations,
    active_configurations_count: productInfo.value.active_configurations_count,
    min_cost: productInfo.value.min_cost,
    product_id: productInfo.value.id,
    vendor_model: productInfo.value.vendor_model,
    start_at_cost: configurationStartAtPrice.value,
    product_configuration_id: configurationInfo.value?.id,
  });
}

async function onUpdateWishlist() {
  if (!productInfo.value) return;
  try {
    // const image = await mergeImages([
    //   { src: configurationInfo.value?.medias?.[0]?.file?.url },
    // ]);
    const image = configurationInfo.value?.medias?.[0]?.file?.url || "";
    await updateWishlist({
      sku: configurationInfo.value?.configuration_id || "",
      images: [image || ""],
      cost: configurationInfo.value?.cost ? +configurationInfo.value.cost : 0,
      slug: productInfo.value?.slug,
      configuration_id: configurationInfo.value?.configuration_id || "",
      engraving: {},
      materials: productInfo.value?.materials || [],
      material_categories: productInfo.value?.material_categories,
      material_categories_plp_hover_ids:
        productInfo.value?.material_categories_plp_hover_ids,
      translations: productInfo.value.translations,
      active_configurations_count:
        productInfo.value.active_configurations_count,
      min_cost: productInfo.value.min_cost,
      product_id: productInfo.value.id,
      vendor_model: productInfo.value.vendor_model,
      start_at_cost: configurationStartAtPrice.value,
      product_configuration_id: configurationInfo.value?.id,
    });
  } catch (error) {
    if (error instanceof ImageMergeError) {
      openSystemDialog({
        type: "alert",
        title: `${t("error")}!`,
        text: `${t("failedLoadImage")}!`,
      });
    }
  }
}

function onSetRecentlyViewed() {
  const currentTranslationId =
    generalStore.getCurrentLanguage(locale.value as string)?.id ||
    generalStore.getDefaultLanguage?.id;

  if (!configurationPrice.value || !images.value?.[0] || !currentSku.value) {
    return;
  }
  if (productInfo.value) {
    const tempData = {
      ...productInfo.value,
      default_config: {
        materials: configurationInfo.value?.materials.map((el) =>
          productInfo.value?.materials?.find((i) => i.id === el),
        ),
      },
      variant_plp_image: {
        cost: configurationPrice.value,
        image: images.value?.[0] || {},
        sku: currentSku.value,
        translations: [
          {
            title: productTitle.value,
            description: productDescription.value,
            language_id: currentTranslationId,
          },
        ],
      },
    };

    setRecentlyViewed(tempData as unknown as CatalogData);
  }
}

function setViewItemEvent(data) {
  if (!data || !productInfo.value) {
    visitEvent();
    return;
  }
  const category = (productInfo.value as ProductData)?.categories?.[0];
  viewItemEvent(productInfo.value as ProductData, data, images.value);
  dynamicParamsEvent("View Product", {
    ecomm_prodid: data?.configuration_id,
    ecomm_pagetype: "product",
    ecomm_totalvalue: eventsPriceFormat(data?.base_cost),
    ecomm_category: category?.currentTranslation?.title || "",
  });
  visitEvent(false, {
    product: productInfo.value,
    configuration: data,
  });
}

function usePageSeo() {
  const seoTitle = computed(
    () =>
      `${productInfo.value?.currentTranslation?.meta_title || productTitle.value} | ${generalStore.settings?.company_name}`,
  );

  const seoDescription = computed(() => {
    return `${t("shop")} ${seoTitle.value} ${t("metaDescription")}`;
  });
  return {
    seoTitle,
    seoDescription,
  };
}

function parseAdditionalProperties(string: string) {
  return string.split(";").reduce(
    (acc, item) => {
      if (item) {
        const [property, value] = item.split("|").map((str) => str.trim());
        if (property && value) {
          acc.push({ property, value });
        }
      }
      return acc;
    },
    [] as { property: string; value: string }[],
  );
}

function useFullBreadcrumbs() {
  const navigation = computed(() => {
    return toTree(headerMenu);
  });

  const prevPage =
    typeof router.options.history.state.back === "string"
      ? router.options.history.state.back
      : "";
  const prevPageResolved = router.resolve(prevPage);

  const comeFrom = headerMenu.find(
    (menuEl) =>
      menuEl.url ===
      router.resolve({
        path: prevPageResolved.path,
        hash: prevPageResolved.hash,
      }).fullPath,
  );

  const resolvedPath = router.resolve(comeFrom?.url || "");
  const fullBreadcrumbs = findBreadcrumb(navigation.value, resolvedPath);
  const productTitleComputed = computed(() => {
    if (isEngagementRing.value) {
      return !isStartCustomizing.value
        ? productTitle.value || ""
        : t("designYourEngagementRing");
    } else {
      return productTitle.value;
    }
  });

  function setNewBreadcrumbs() {
    setBreadcrumbs([
      ...[
        { text: t("home"), href: "/", disabled: false },
        ...(fullBreadcrumbs?.length
          ? fullBreadcrumbs.map((el) => ({ ...el, disabled: false }))
          : []),
      ],
      {
        text: productTitleComputed.value,
        href: `/product/${productInfo.value?.slug}`,
        disabled: true,
      },
    ]);
  }

  return { setNewBreadcrumbs };
}

function useSticky(offsetTop: number) {
  const customEvent = ref<Event | null>(null);
  const leftSide = ref<HTMLDivElement>();
  const rightSide = ref<HTMLDivElement>();
  const { height: leftHeight } = useElementSize(leftSide);
  const { height: rightHeight } = useElementSize(rightSide);
  const stickyOffsetTop = ref(offsetTop);
  const stickySide = ref("");

  function setStickySide() {
    if (leftHeight.value && rightHeight.value) {
      if (leftHeight.value + stickyOffsetTop.value > rightHeight.value) {
        stickySide.value = "right";
      } else {
        stickySide.value = "left";
      }
    } else {
      stickySide.value = "left";
    }
  }

  watch(leftHeight, async () => {
    if (customEvent.value) {
      await nextTick();
      setStickySide();
      setTimeout(() => {
        window.dispatchEvent(customEvent.value as Event);
      }, 0);
    }
  });

  watch(rightHeight, async () => {
    if (customEvent.value) {
      await nextTick();
      setStickySide();
      setTimeout(() => {
        window.dispatchEvent(customEvent.value as Event);
      }, 0);
    }
  });

  const unwatch = watchEffect(async () => {
    if (leftSide.value && rightSide.value) {
      customEvent.value = new Event("pdpResizeEvent");

      await nextTick(() => {
        setStickySide();
      });
      unwatch();
    }
  });

  return {
    leftSide,
    rightSide,
    customEvent,
    leftHeight,
    rightHeight,
    stickyOffsetTop,
    setStickySide,
    stickySide,
  };
}

function useProductDetails() {
  const detailsMappedData = computed(() => {
    if (
      !productInfo.value?.currentTranslation?.content?.details_map &&
      !configurationInfo.value?.currentTranslation?.additional_details
    ) {
      return [];
    }
    if (configurationInfo.value?.currentTranslation?.additional_details) {
      const additionalDetails =
        configurationInfo.value.currentTranslation.additional_details;
      return parseAdditionalProperties(additionalDetails);
    }
    return productInfo.value?.currentTranslation?.content?.details_map.map(
      (el) => {
        return {
          property: el.property,
          value: replacePlaceholder(
            el.value,
            (materialSKU: string) =>
              allSelectedMaterials.value?.[
                materialCategoriesDictionary.value?.[materialSKU]
              ]?.currentTranslation?.title || "",
          ),
        };
      },
    );
  });

  watchEffect(() => {
    dynamicProductTitle.value = replacePlaceholder(
      (productInfo.value as ProductData)?.currentTranslation?.dynamic_title ||
        "",
      (materialSKU: string) =>
        allSelectedMaterials.value?.[
          materialCategoriesDictionary.value?.[materialSKU]
        ]?.currentTranslation?.title || "",
    );
  });
  return {
    detailsMappedData,
  };
}

function useProductValidation() {
  const productSchema = computed(() => {
    if (
      SizeMaterialCategory.value &&
      (Object.keys(additionalOptions.value)?.includes(
        SizeMaterialCategory.value.slug,
      ) ||
        Object.keys(result.value)?.includes(SizeMaterialCategory.value.slug))
    ) {
      return object().shape({
        [SizeMaterialCategory.value.slug]: string().required(
          t("pleaseSelectSize"),
        ),
      });
    } else {
      return {};
    }
  });

  const { validate } = useForm({
    validationSchema: productSchema,
  });

  return {
    validate,
  };
}

async function handleEngagementFilterClick(
  type: "optionsFilterClick" | "filterClick",
  categorySlug: string,
  item: any,
) {
  isStartCustomizing.value = false;
  setNewBreadcrumbs();
  if (type === "optionsFilterClick") {
    await onOptionsFilterClick(categorySlug, item);
  } else {
    await onFilterClick(categorySlug, item);
    latestChangedCategory.value = categorySlug;
  }

  await router.replace({
    path: route.path,
    query: {
      ...route.query,
      start_customizing: undefined,
    },
  });
}

function useNativeStickyEl() {
  const isStuck = ref(false);
  const stickyEl = ref();
  watchEffect(() => {
    if (stickyEl.value) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          isStuck.value = entry.boundingClientRect.top <= 66;
        },
        {
          rootMargin: `-66px 0px 0px 0px`,
          threshold: [0, 1],
        },
      );

      observer.observe(stickyEl.value);
    }
  });

  return {
    isStuck,
    stickyEl,
  };
}

function createAffirmMessage() {
  clearTimeout(affirmTimeout);
  if (canInitAffirmMessage.value) {
    affirmTimeout = setTimeout(() => {
      createAffirmMessageElement(finalConfigurationPriceValue.value * 100);
    }, 150);
  }
}
</script>
